import { useState, useCallback, memo, useEffect, useRef } from 'react';
import { useSidebar } from '@/context/SidebarContext';
import { EarlyWarningSidebar } from '@/components/shared/Sidebar/EarlyWarningSidebar';
import { Container } from '@/components/Layout/Container';
import { EarlyWarningHeader } from '@/components/Layout/Header/EarlyWarningHeader';
import { useEarlyWarning } from '@/context/EarlyWarningContext';
import { cn } from '@/lib/utils';
import { Plus } from 'lucide-react';
import {
  EarlyWarningData,
  EarlyWarning,
  SavedEarlyWarning,
} from '@/types/early-warning';
import {
  DataVisualizations,
  EarlyWarningTable,
  EarlyWarningFormModal,
} from '@/components/EarlyWarning';
import { useEarlyWarningFilters } from '@/hooks/useEarlyWarningFilters';
import { captureEvent, EVENTS, TOOL_IDS, TOOL_NAMES } from '@/lib/posthog';

const MemoizedEarlyWarningHeader = memo(EarlyWarningHeader);

const ContentSection = memo(
  ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="border-b pb-4 last:border-b-0 last:pb-0 last:pt-4">
      <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-4">{title}</h2>
      {children}
    </div>
  )
);

ContentSection.displayName = 'ContentSection';

export function EarlyWarningRegister() {
  const { isCollapsed } = useSidebar();
  const { dispatch } = useEarlyWarning();
  const hasTrackedView = useRef(false);

  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');
  const [openWarningForm, setOpenWarningForm] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState<EarlyWarning | null>(
    null
  );
  const [warningData, setWarningData] = useState<EarlyWarning[]>([]);

  // Track tool view on mount
  useEffect(() => {
    if (!hasTrackedView.current) {
      captureEvent(EVENTS.TOOL_VIEWED, {
        tool_id: TOOL_IDS.EARLY_WARNING,
        tool_name: TOOL_NAMES[TOOL_IDS.EARLY_WARNING],
      });
      hasTrackedView.current = true;
    }
  }, []);

  const {
    sortField,
    sortDirection,
    statusFilter,
    handleSort,
    setStatusFilter,
    filteredData,
  } = useEarlyWarningFilters(warningData);

  const currentData: EarlyWarningData = {
    projectName,
    clientName,
    projectStartDate,
    projectEndDate,
    warnings: warningData,
  };

  useEffect(() => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: currentData,
    });
  }, [projectName, clientName, projectStartDate, projectEndDate, warningData, dispatch]);

  const handleAddWarning = useCallback(() => {
    setSelectedWarning(null);
    setOpenWarningForm(true);
  }, []);

  const handleEditWarning = useCallback((warning: EarlyWarning) => {
    setSelectedWarning(warning);
    setOpenWarningForm(true);
  }, []);

  const handleSaveWarning = useCallback(
    (warning: EarlyWarning) => {
      if (selectedWarning) {
        setWarningData(
          warningData.map((w) =>
            w.id === selectedWarning.id
              ? { ...warning, id: selectedWarning.id }
              : w
          )
        );
        captureEvent(EVENTS.EARLY_WARNING_UPDATED, {
          tool_id: TOOL_IDS.EARLY_WARNING,
          tool_name: TOOL_NAMES[TOOL_IDS.EARLY_WARNING],
          project_name: projectName,
        });
      } else {
        setWarningData([
          ...warningData,
          { ...warning, id: `EW-${warningData.length + 1}` },
        ]);
        captureEvent(EVENTS.EARLY_WARNING_CREATED, {
          tool_id: TOOL_IDS.EARLY_WARNING,
          tool_name: TOOL_NAMES[TOOL_IDS.EARLY_WARNING],
          project_name: projectName,
        });
      }
      setOpenWarningForm(false);
    },
    [selectedWarning, warningData, projectName]
  );

  const downloadCSV = useCallback(() => {
    if (!warningData.length) return;
    const headers = [
      'ID',
      'Reference',
      'From',
      'Title',
      'Reply Due',
      'Price',
      'Completion',
      'Key Date',
      'Performance',
      'Cost',
      'Score',
      'Status',
    ];
    const rows = filteredData.map((warning) => [
      warning.id,
      warning.reference || '',
      warning.role,
      warning.title,
      warning.replyDue,
      warning.impacts?.increasePrices ? '✓' : '',
      warning.impacts?.delayCompletion ? '✓' : '',
      warning.impacts?.delayKeyDate ? '✓' : '',
      warning.impacts?.impairPerformance ? '✓' : '',
      warning.impacts?.increaseContractorCost ? '✓' : '',
      warning.score,
      warning.status,
    ]);
    let csvContent = headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.join(',') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'early_warnings_register.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    captureEvent(EVENTS.EARLY_WARNING_EXPORTED, {
      tool_id: TOOL_IDS.EARLY_WARNING,
      tool_name: TOOL_NAMES[TOOL_IDS.EARLY_WARNING],
      export_format: 'csv',
      project_name: projectName,
    });
  }, [warningData, filteredData, projectName]);

  const handleLoad = useCallback(
    (calculation: SavedEarlyWarning) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: calculation.data,
      });
      if (calculation.data) {
        const data = calculation.data;
        if (data.projectName) setProjectName(data.projectName);
        if (data.clientName) setClientName(data.clientName);
        if (data.projectStartDate) setProjectStartDate(data.projectStartDate);
        if (data.projectEndDate) setProjectEndDate(data.projectEndDate);
        if (data.warnings && Array.isArray(data.warnings))
          setWarningData(data.warnings);
      }
    },
    [dispatch]
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MemoizedEarlyWarningHeader className="px-2 sm:px-4" />
      <div className="flex flex-1 flex-col md:flex-row">
        <EarlyWarningSidebar
          data={currentData}
          onLoad={handleLoad}
          className={cn(
            'w-full overflow-y-auto border-b transition-all duration-300',
            'md:border-r md:border-b-0',
            isCollapsed ? 'md:w-20' : 'md:w-80'
          )}
        />
        <main
          className={cn(
            'flex-1 overflow-y-auto transition-all duration-300',
            isCollapsed ? 'md:ml-20' : 'md:ml-80'
          )}
          role="main"
        >
          <Container className="px-2 sm:px-4">
            <section className="space-y-4 sm:space-y-8 py-4 sm:py-6">
              <ContentSection title="Project Details">
                <div className="rounded-xl border bg-card text-card-foreground shadow transition hover:shadow-lg p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="projectName"
                        className="text-sm font-medium"
                      >
                        Project Name
                      </label>
                      <input
                        id="projectName"
                        type="text"
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        placeholder="Enter project name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="clientName"
                        className="text-sm font-medium"
                      >
                        Client Name
                      </label>
                      <input
                        id="clientName"
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        placeholder="Enter client name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Project Start Date
                      </label>
                      <input
                        type="date"
                        value={projectStartDate}
                        onChange={(e) => setProjectStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Project End Date
                      </label>
                      <input
                        type="date"
                        value={projectEndDate}
                        onChange={(e) => setProjectEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                    </div>
                  </div>
                </div>
              </ContentSection>

              <ContentSection title="Early Warnings Register">
                <div className="flex justify-end mb-4">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="overdue">Overdue</option>
                    <option value="avoided">Avoided</option>
                  </select>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={handleAddWarning}
                    className="flex items-center px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Early Warning
                  </button>
                  <button
                    onClick={downloadCSV}
                    className="flex items-center px-3 py-1 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  >
                    Download CSV
                  </button>
                </div>
                <EarlyWarningTable
                  filteredData={filteredData}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  handleSort={handleSort}
                  handleEditWarning={handleEditWarning}
                />
              </ContentSection>

              <DataVisualizations warningData={warningData} />
            </section>
          </Container>
        </main>
      </div>
      {openWarningForm && (
        <EarlyWarningFormModal
          selectedWarning={selectedWarning}
          onClose={() => setOpenWarningForm(false)}
          onSave={handleSaveWarning}
        />
      )}
    </div>
  );
}
